import { createAppSlice } from '../createAppSlice';
import { PAGINATION } from '@/constants/pagination.constant';
import { transformerArray, transformerObject } from '../transformer';
import { MessageType } from '@/types/common';
import { Message } from '@/enums/message.enum';
import { DESCRIPTION, MESSAGE } from '@/constants/message.constant';

export type RoleSliceState = {
  loading: boolean;
  roleList: UserRole[];
  roleDetail?: RoleDetail;
  pagination: Pagination;
  message?: MessageType;
};

const initialState: RoleSliceState = {
  loading: false,
  roleList: [],
  pagination: PAGINATION
};

export const roleSlice = createAppSlice({
  name: 'roles',
  initialState,
  reducers: (create) => ({
    getListRoles: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.get('role', { query });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.roleList = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.roleList = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    getRoleById: create.asyncThunk(
      async (roleId: string) => {
        const response = await apiFetcher.post(`role/detail`, {
          roleId
        });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.roleDetail = transformerObject(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.roleDetail = undefined;
        }
      }
    ),
    createRole: create.asyncThunk(
      async (newRoleData: CreateRoleProps) => {
        await apiFetcher.post('role/create', {
          name: newRoleData.name,
          desc: newRoleData.desc,
          modulePermission: JSON.stringify(newRoleData.modulePermission)
        });
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_ROLE_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_ROLE_FAIL,
            description: DESCRIPTION.ROLE_EXIST
          };
        }
      }
    ),
    updateRole: create.asyncThunk(
      async ({
        roleId,
        roleData
      }: {
        roleId: number;
        roleData: CreateRoleProps;
      }) => {
        await apiFetcher.post(`role/update`, {
          id: roleId,
          desc: roleData.desc,
          name: roleData.name,
          modulePermission: JSON.stringify(roleData.modulePermission)
        });
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_ROLE_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_ROLE_FAIL
          };
        }
      }
    ),
    deleteRole: create.asyncThunk(
      async (roleId: number) => {
        await apiFetcher.post(`role/delete`, {
          id: roleId
        });
        return roleId;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.roleList = state.roleList.filter(
            (role) => role.id !== action.payload
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_ROLE_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_ROLE_FAIL
          };
        }
      }
    ),

    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),

  selectors: {
    watchRoleList: (role) => role.roleList,
    watchRoleLoading: (role) => role.loading,
    watchRoleDetail: (role) => role.roleDetail,
    watchRolePagination: (role) => role.pagination,
    watchRoleMessage: (role) => role.message
  }
});

export const {
  watchRoleList,
  watchRoleLoading,
  watchRolePagination,
  watchRoleMessage,
  watchRoleDetail
} = roleSlice.selectors;

export const {
  resetMessages,
  getListRoles,
  createRole,
  updateRole,
  deleteRole,
  getRoleById
} = roleSlice.actions;
